// src/main.js — Vue 3 (compat build) + Router 4 + Vuetify 2 + i18n v9

import { createApp } from 'vue'
import App from './App.vue'

import vuetify from './plugins/vuetify'
import { installVeeValidateBridge } from './plugins/vee-validate-bridge'

import 'roboto-fontface/css/roboto/roboto-fontface.css'
import '@mdi/font/css/materialdesignicons.css'
import '@babel/polyfill'
import '@/assets/style.css'

import axios from 'axios'
import router from './router'
import dayjs from 'dayjs'

import VueLazyload from 'vue3-lazyload'
import i18n from './i18n'
import FlagIcon from 'vue-flag-icon'
import VueCookies from 'vue-cookies'
import DatetimePicker from 'vuetify-datetime-picker'


import EncryptValue from '@/plugins/encrypt'
import getSocialNetwork from './plugins/getSocialNetwork'

const app = createApp(App)

app.config.compatConfig = {
  MODE: 2,
  GLOBAL_MOUNT: 'suppress-warning',
  GLOBAL_EXTEND: 'suppress-warning',
  GLOBAL_PROTOTYPE: 'suppress-warning',
  RENDER_FUNCTION: true,
  COMPONENT_ASYNC: 'suppress-warning',
  INSTANCE_LISTENERS: 'suppress-warning',
  ATTR_FALSE_VALUE: 'suppress-warning'
}


// ---- Globais
app.config.globalProperties.$getSocialNetwork = getSocialNetwork
app.config.globalProperties.$encryptValue = EncryptValue
app.config.globalProperties.$fatalError = true
app.config.globalProperties.$i18n = {
  get locale () {
    const l = i18n.global.locale
    return typeof l === 'string' ? l : l.value
  },
  set locale (v) {
    const l = i18n.global.locale
    if (typeof l === 'string') i18n.global.locale = v
    else l.value = v
  },
  t: (...args) => i18n.global.t(...args)
}

// ---- Shim de sessão (substitui 'vue-session' de Vue 2)
const SessionPlugin = {
  install (app) {
    const s = window.sessionStorage
    const api = {
      start () {},
      set: (k, v) => s.setItem(String(k), JSON.stringify(v)),
      get: (k) => {
        const raw = s.getItem(String(k))
        try { return JSON.parse(raw) } catch { return raw }
      },
      has: (k) => s.getItem(String(k)) !== null,
      remove: (k) => s.removeItem(String(k)),
      clear: () => s.clear()
    }
    app.config.globalProperties.$session = api
    app.provide('session', api)
  }
}

// ---- Stub leve para <gb-social-button> (remove dependência não compatível)
app.component('gb-social-button', {
  name: 'GbSocialButton',
  props: { network: String, size: String, reverse: Boolean, theme: String },
  emits: ['click'],
  computed: {
    icon () {
      const m = (this.network || '').toLowerCase()
      return m === 'linkedin' ? 'mdi-linkedin'
        : m === 'facebook' ? 'mdi-facebook'
        : m === 'twitter' ? 'mdi-twitter'
        : m === 'github' ? 'mdi-github'
        : 'mdi-share-variant'
    },
    fontSize () {
      return this.size === 'large' ? '28px' : this.size === 'medium' ? '22px' : '18px'
    }
  },
  template: `
    <button type="button" class="gb-social-button__btn"
      @click="$emit('click')"
      style="background:transparent;border:none;cursor:pointer;display:inline-flex;align-items:center">
      <i class="mdi" :class="icon" :style="{fontSize}"></i>
    </button>
  `
})

// ---- Gtag minimal (substitui 'vue-gtag' no compat)
const GtagPlugin = {
  install (app, { id, params = {}, router } = {}) {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || []
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments) }
      if (id) {
        window.gtag('js', new Date())
        window.gtag('config', id, params)
      }
    }
    const api = {
      event: (...args) => window.gtag && window.gtag('event', ...args),
      config: (...args) => window.gtag && window.gtag('config', ...args),
      set: (...args) => window.gtag && window.gtag('set', ...args)
    }
    app.config.globalProperties.$gtag = api
    if (router && id && typeof router.afterEach === 'function') {
      router.afterEach((to) => {
        window.gtag && window.gtag('config', id, { page_path: to.fullPath })
      })
    }
  }
}

// ---- Plugins
installVeeValidateBridge(app)

app.use(vuetify)
app.use(VueLazyload)
app.use(SessionPlugin)
app.use(VueCookies, { expires: '7d' })
app.use(FlagIcon)
app.use(DatetimePicker)
app.use(i18n)
app.use(router)

app.use(GtagPlugin, {
  id: 'G-ZQ4F9N5ZWQ',
  params: { send_page_view: true },
  router
})

// ---- Axios
axios.interceptors.request.use(
  (config) => {
    config.withCredentials = true
    config.headers['Content-Type'] = 'application/json'
    config.headers['Accept'] = 'application/json'
    return config
  },
  (error) => Promise.reject(error)
)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) router.push('/')
    return Promise.reject(error)
  }
)

// ---- Mixin global
app.mixin({
  methods: {
    setTheme (theme) {
      const dark = theme?.dark || {}
      const light = theme?.light || {}
      if (this.$vuetify?.theme?.themes?.dark && this.$vuetify?.theme?.themes?.light) {
        Object.keys(dark).forEach(k => { this.$vuetify.theme.themes.dark[k] = dark[k] })
        Object.keys(light).forEach(k => { this.$vuetify.theme.themes.light[k] = light[k] })
        this.$vuetify.groupSelectedColor = this.$vuetify.theme.dark
          ? theme.dark?.primaryLight
          : theme.light?.primaryLight
      }
    },
    formatDate (date) {
      return date == null ? null : dayjs(date).format('DD/MM/YYYY')
    }
  }
})

app.mount('#app')
